import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { PageLoadingIndicator } from '@kioskgaming/page-loading';
import { formatInTimezone, useTimezone } from '@kioskgaming/ui';
import AppLayout from '../components/layout/AppLayout';
import {
  fetchMessagingCampaignDetail,
  type MessagingCampaignDetail,
  type MessagingCampaignRecipientRow,
} from '../services/api';

function formatUsd(n: number | null | undefined): string {
  if (n == null || !Number.isFinite(n)) return '—';
  return `$${n.toFixed(2)}`;
}

function formatDate(iso: string | null | undefined, timezone: string): string {
  return formatInTimezone(iso, timezone);
}

export default function MessagesCampaignDetailPage() {
  const { timezone } = useTimezone();
  const { campaignId } = useParams<{ campaignId: string }>();
  const [detail, setDetail] = useState<MessagingCampaignDetail | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const res = await fetchMessagingCampaignDetail(campaignId);
      if (res.success && res.data) setDetail(res.data);
    } catch {
      toast.error('Could not load campaign');
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  useEffect(() => {
    void load();
    const timer = setInterval(() => {
      if (detail?.campaign.status === 'queued' || detail?.campaign.status === 'processing') {
        void load();
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [load, detail?.campaign.status]);

  if (!campaignId) {
    return (
      <AppLayout>
        <p className="text-sm text-stone-500">Invalid campaign.</p>
      </AppLayout>
    );
  }

  if (loading && !detail) {
    return (
      <AppLayout>
        <PageLoadingIndicator />
      </AppLayout>
    );
  }

  if (!detail) {
    return (
      <AppLayout>
        <p className="text-sm text-stone-500">Campaign not found.</p>
      </AppLayout>
    );
  }

  const { campaign, recipients } = detail;

  return (
    <AppLayout>
      <div className="mb-4">
        <Link to="/messages/history" className="text-sm text-emerald-700 hover:underline">
          ← Back to history
        </Link>
        <h1 className="text-xl font-semibold text-stone-900 mt-2">Campaign details</h1>
        <p className="text-sm text-stone-600 mt-1 capitalize">
          {campaign.channel} · {campaign.status} · {formatDate(campaign.createdAt, timezone)}
        </p>
      </div>

      <div className="grid gap-4 md:grid-cols-2 mb-6">
        <div className="rounded-lg border border-stone-200 p-4 text-sm space-y-1">
          <p>
            <span className="text-stone-500">Quoted:</span> {formatUsd(campaign.quotedCostUsd)}
          </p>
          <p>
            <span className="text-stone-500">Final cost:</span> {formatUsd(campaign.finalCostUsd)}
          </p>
          <p>
            <span className="text-stone-500">Sent / eligible:</span> {campaign.sentCount} /{' '}
            {campaign.eligibleCount}
          </p>
          {campaign.failedCount > 0 && (
            <p className="text-red-600">Failed: {campaign.failedCount}</p>
          )}
        </div>
        <div className="rounded-lg border border-stone-200 p-4 text-sm">
          {campaign.channel === 'email' && campaign.subject && (
            <p className="font-medium mb-2">Subject: {campaign.subject}</p>
          )}
          <p className="text-stone-700 whitespace-pre-wrap">{campaign.body || campaign.bodyPreview}</p>
        </div>
      </div>

      <h2 className="text-sm font-semibold text-stone-800 mb-2">Delivery report</h2>
      <div className="overflow-x-auto rounded-lg border border-stone-200">
        <table className="min-w-full text-sm">
          <thead className="bg-stone-50 text-left text-stone-600">
            <tr>
              <th className="px-3 py-2 font-medium">Player</th>
              <th className="px-3 py-2 font-medium">Destination</th>
              <th className="px-3 py-2 font-medium">Status</th>
              <th className="px-3 py-2 font-medium">Cost</th>
              <th className="px-3 py-2 font-medium">Sent at</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-100">
            {recipients.map((r: MessagingCampaignRecipientRow) => (
              <tr key={r.id}>
                <td className="px-3 py-2">{r.playerUsername || r.playerId.slice(0, 8)}</td>
                <td className="px-3 py-2">{r.destination || '—'}</td>
                <td className="px-3 py-2 capitalize">{r.deliveryStatus.replace(/_/g, ' ')}</td>
                <td className="px-3 py-2">{formatUsd(r.costUsd)}</td>
                <td className="px-3 py-2">{formatDate(r.sentAt, timezone)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </AppLayout>
  );
}
